import { useState, type DragEvent } from "react";
import {
  ChevronRight,
  ChevronDown,
  Folder as FolderIcon,
  GripVertical,
  MoreVertical,
  FolderPlus,
  Pencil,
  Trash2,
} from "lucide-react";
import type { Folder } from "../../types/contract";
import { useUiStore, DND_FIELD, DND_FOLDER } from "../../stores/uiStore";
import {
  useFolders,
  useRenameFolder,
  useDeleteFolder,
  useCreateFolder,
  useMoveField,
  useReparentFolder,
} from "../../hooks/queries";
import { InlineEdit } from "../../components/InlineEdit";
import { Menu, MenuItem } from "../../ui/Menu";

function isDescendant(
  nodeId: string,
  ancestorId: string,
  folders: Folder[],
): boolean {
  const parentOf = new Map(
    folders.map((f) => [f.folder_id, f.parent_folder_id]),
  );
  const seen = new Set<string>();
  let cur: string | null | undefined = nodeId;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    if (cur === ancestorId) return true;
    cur = parentOf.get(cur) ?? null;
  }
  return false;
}

// A folder header: a ⠿ drag handle (nest this folder under another), expand/collapse
// inline rename, count, per-folder menu, and a native-DnD drop target that accepts a
// dragged field (move into folder) or another folder (reparent — cycle-guarded both
// client-side here AND server-side). Layout-agnostic (a div); the table wraps it in a
// full-width cell, mobile renders it directly. (Front-End.md §A10.)
export function FolderRow({
  folder,
  depth,
  fieldCount,
  subfolderCount,
  selectAll,
  selectSome,
  onToggleSelect,
  selectDisabled,
  forceExpanded = false,
}: {
  folder: Folder;
  depth: number;
  fieldCount: number;
  subfolderCount: number;
  // folder-level selection: select/deselect every field under this folder at once.
  selectAll?: boolean;
  selectSome?: boolean;
  onToggleSelect?: () => void;
  selectDisabled?: boolean;
  // A folder-name search draws this folder open without writing the saved collapse set.
  forceExpanded?: boolean;
}) {
  const storedCollapsed = useUiStore((s) =>
    s.collapsedFolders.has(folder.folder_id),
  );
  const collapsed = forceExpanded ? false : storedCollapsed;
  const toggleFolder = useUiStore((s) => s.toggleFolder);
  const isDragOver = useUiStore((s) => s.dragOverFolderId === folder.folder_id);
  const setDragOver = useUiStore((s) => s.setDragOverFolder);
  const { data: folders } = useFolders();
  const renameFolder = useRenameFolder();
  const deleteFolder = useDeleteFolder();
  const createFolder = useCreateFolder();
  const moveField = useMoveField();
  const reparent = useReparentFolder();
  const [menu, setMenu] = useState(false);
  const [autoRename, setAutoRename] = useState(false);

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
    const fieldId = e.dataTransfer.getData(DND_FIELD);
    if (fieldId) {
      moveField.mutate({ fieldId, folderId: folder.folder_id });
      return;
    }
    const dragFolderId = e.dataTransfer.getData(DND_FOLDER);
    // nest a folder — but never into itself or one of its own descendants (a cycle)
    if (
      dragFolderId &&
      dragFolderId !== folder.folder_id &&
      !isDescendant(folder.folder_id, dragFolderId, folders ?? [])
    ) {
      reparent.mutate({
        folderId: dragFolderId,
        parentFolderId: folder.folder_id,
      });
    }
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(folder.folder_id);
      }}
      onDragLeave={() => setDragOver(null)}
      onDrop={onDrop}
      style={{ paddingLeft: 8 + depth * 16 }}
      className={`flex items-center gap-1.5 py-1.5 pr-2 text-sm ${isDragOver ? "bg-white/10 ring-1 ring-inset ring-white/40" : ""}`}
    >
      {onToggleSelect && (
        // `indeterminate` was already being set correctly — verified by test — but this app paints
        // native controls with `accent-color: #fafafa` against a dark page, and the browser's
        // indeterminate mark doesn't survive that. So the box is drawn here instead of trusted to
        // the platform: `appearance-none` plus a glyph we control, filled for BOTH checked and
        // partial states (a dark dash on an unfilled dark box is why "set the property" wasn't
        // enough).
        <span className="relative inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
          <input
            type="checkbox"
            className={`h-3.5 w-3.5 appearance-none rounded-[3px] border disabled:opacity-40 ${
              selectAll || selectSome
                ? "border-fg bg-fg"
                : "border-line bg-transparent"
            }`}
            checked={!!selectAll}
            disabled={selectDisabled}
            ref={(el) => {
              if (el) el.indeterminate = !selectAll && !!selectSome;
            }}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select all fields in ${folder.name}`}
            title={
              selectDisabled
                ? "No fields in this folder"
                : `Select all fields in ${folder.name}`
            }
          />
          {(selectAll || selectSome) && (
            <span
              aria-hidden
              className="pointer-events-none absolute text-[10px] font-bold leading-none text-canvas"
            >
              {selectAll ? "\u2713" : "\u2013"}
            </span>
          )}
        </span>
      )}
      <span
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(DND_FOLDER, folder.folder_id);
          e.dataTransfer.effectAllowed = "move";
        }}
        onClick={(e) => e.stopPropagation()}
        className="shrink-0 cursor-grab text-faint hover:text-fg active:cursor-grabbing"
        title="Drag onto another folder to nest it"
        aria-label="Drag folder to nest it"
      >
        <GripVertical size={13} />
      </span>
      <button
        onClick={() => {
          // The search override is view-only. Writing collapse here would stick after the query is cleared.
          if (forceExpanded) return;
          toggleFolder(folder.folder_id);
        }}
        className="rounded p-0.5 text-muted hover:bg-hover"
        aria-label={collapsed ? "Expand folder" : "Collapse folder"}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </button>
      <FolderIcon size={14} className="text-muted" />
      <InlineEdit
        value={folder.name}
        autoStart={autoRename}
        onDone={() => setAutoRename(false)}
        onCommit={(name) =>
          renameFolder.mutate({ folderId: folder.folder_id, name })
        }
        className="font-medium text-fg"
        title="Double-click to rename folder"
      />
      <span className="text-xs text-faint">
        {fieldCount} field{fieldCount === 1 ? "" : "s"}
        {subfolderCount
          ? ` · ${subfolderCount} folder${subfolderCount === 1 ? "" : "s"}`
          : ""}
      </span>
      <div className="ml-auto">
        <Menu
          open={menu}
          onOpenChange={setMenu}
          align="end"
          closeOnEscape={false}
          scrim
          className="z-menu w-40 rounded-md border border-line bg-raised py-1 text-xs text-fg shadow-lg"
          trigger={
            <button
              className="rounded p-1 text-muted hover:bg-hover"
              aria-label="Folder menu"
            >
              <MoreVertical size={14} />
            </button>
          }
        >
          <MenuItem
            className="flex w-full cursor-pointer items-center gap-2 rounded-none px-3 py-1.5 text-xs text-fg outline-none data-[highlighted]:bg-hover"
            onSelect={() => {
              setMenu(false);
              createFolder.mutate({
                name: "New folder",
                parentFolderId: folder.folder_id,
              });
            }}
          >
            <FolderPlus size={13} /> New subfolder
          </MenuItem>
          <MenuItem
            className="flex w-full cursor-pointer items-center gap-2 rounded-none px-3 py-1.5 text-xs text-fg outline-none data-[highlighted]:bg-hover"
            onSelect={() => {
              setMenu(false);
              setAutoRename(true);
            }}
          >
            <Pencil size={13} /> Rename
          </MenuItem>
          <MenuItem
            className="flex w-full cursor-pointer items-center gap-2 rounded-none px-3 py-1.5 text-xs text-red-400 outline-none data-[highlighted]:bg-hover"
            onSelect={() => {
              setMenu(false);
              deleteFolder.mutate(folder.folder_id);
            }}
          >
            <Trash2 size={13} /> Delete
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
}
